import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import apiClient from "../api/apiClient";
import Select from "react-select";
import SearchSelect from "./SearchSelect";
import ExtractoCuentasContratoModal from "./ExtractoCuentasContratoModal";

const asText = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * Extrae el número de la unidad desde su descripción (ItemName de REM_UNI),
 * p. ej. "DEPARTAMENTO 319" -> "319". Prioriza el número que sigue a una
 * palabra clave (dpto/depto/departamento/unidad) y, si no hay, toma el primero.
 */
const extraerNroUnidad = (desc) => {
  const s = asText(desc).trim();
  if (!s) return "";
  const conClave = s.match(/(?:dptos?|deptos?|departamentos?|unidad)\.?\s*(?:n[°ºo]?\.?\s*)?(\d+[A-Za-z]?)/i);
  if (conClave) return conClave[1];
  const suelto = s.match(/\d+[A-Za-z]?/);
  return suelto ? suelto[0] : "";
};

const formatDate = (value) => {
  if (!value) return "";
  const s = String(value).trim();

  const pad2 = (n) => String(n).padStart(2, "0");

  // Contrato: el SP envía fechas como texto en formato DD/MM/YYYY.
  // No usamos Date() para evitar errores por timezone (ej: -1 día).

  // Caso: DD/MM/YYYY o D/M/YYYY (viene así desde algunos SPs / reportes)
  const mDmy = s.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mDmy) {
    const dd = pad2(mDmy[1]);
    const mm = pad2(mDmy[2]);
    const yyyy = mDmy[3];
    return `${dd}/${mm}/${yyyy}`;
  }

  // Variante: DD-MM-YYYY o D-M-YYYY
  const mDmyDash = s.match(/^\s*(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (mDmyDash) {
    const dd = pad2(mDmyDash[1]);
    const mm = pad2(mDmyDash[2]);
    const yyyy = mDmyDash[3];
    return `${dd}/${mm}/${yyyy}`;
  }

  // Caso típico ISO: YYYY-MM-DD...
  const y = s.slice(0, 4);
  const m = s.slice(5, 7);
  const d = s.slice(8, 10);
  if (/^\d{4}$/.test(y) && /^\d{2}$/.test(m) && /^\d{2}$/.test(d)) return `${d}/${m}/${y}`;

  // Caso: YYYY-MM-DD (sin hora) pero no en posición 0 (ej: '2026-02-11T00:00:00')
  const mIso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (mIso) {
    const yyyy = mIso[1];
    const mm = mIso[2];
    const dd = mIso[3];
    return `${dd}/${mm}/${yyyy}`;
  }

  // Si no coincide ninguno de los formatos esperados, devolvemos el valor tal cual.
  return s;
};

const currencyToIntl = (moneda) => {
  const m = String(moneda || "").trim().toUpperCase();
  if (m === "USD" || m === "US$" || m === "$") return "USD";
  if (m === "GS" || m === "PYG" || m === "₲" || m === "G" || m === "GS.") return "PYG";
  return null;
};

const formatMoney = (value, moneda) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  const currency = currencyToIntl(moneda);
  if (!currency) return `${num.toLocaleString("es-PY")} ${moneda || ""}`.trim();

  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "USD" ? 2 : 0,
  }).format(num);
};

const GestionInmobiliariaContratoDetalle = () => {
  const { docEntry } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =====================
  // Edición de Unidad
  // =====================
  const [unidadEditing, setUnidadEditing] = useState(false);
  const [unidadesOptions, setUnidadesOptions] = useState(() => []);
  const [unidadesLoading, setUnidadesLoading] = useState(false);
  const [unidadesError, setUnidadesError] = useState("");
  const [unidadSelected, setUnidadSelected] = useState(null);
  const [unidadSaving, setUnidadSaving] = useState(false);
  const [unidadMsg, setUnidadMsg] = useState("");

  // =====================
  // Edición de Cuenta contable (U_CTA_FACT)
  // =====================
  const [ctaEditing, setCtaEditing] = useState(false);
  const [ctaOptions, setCtaOptions] = useState(() => []);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [ctaError, setCtaError] = useState("");
  const [ctaSelected, setCtaSelected] = useState(null);
  const [ctaSaving, setCtaSaving] = useState(false);
  const [ctaMsg, setCtaMsg] = useState("");

  // CompanyDB para consultas HANA (TC / tasa de impuesto real)
  const companyDb = useMemo(() => {
    try {
      const raw = localStorage.getItem("sapUser");
      const user = raw ? JSON.parse(raw) : null;
      return user?.CompanyDB || user?.companyDb || user?.companyDB || "";
    } catch {
      return "";
    }
  }, []);

  // Fecha de factura (default: hoy). Formato esperado: YYYY-MM-DD
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const d = new Date();
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  // Condición de pago (SAP: PaymentGroupCode). Default: -1 (Contado)
  const [paymentGroupCode, setPaymentGroupCode] = useState(-1);
  const [condPagos, setCondPagos] = useState(() => []);
  const [condPagosLoading, setCondPagosLoading] = useState(false);
  const [condPagosError, setCondPagosError] = useState("");

  // =====================
  // Distribución de facturas (REM_CONT_DACollection)
  // =====================
  const [bpOptions, setBpOptions] = useState(() => []);
  const [bpOptionsLoading, setBpOptionsLoading] = useState(false);
  const [bpOptionsError, setBpOptionsError] = useState("");

  const [showDistribModal, setShowDistribModal] = useState(false);
  const [distribRows, setDistribRows] = useState(() => []);
  const [distribSaving, setDistribSaving] = useState(false);
  const [distribError, setDistribError] = useState("");
  const [distribMsg, setDistribMsg] = useState("");

  // react-select options
  const bpSelectOptions = useMemo(() => {
    return (bpOptions || [])
      .map((bp) => {
        const code = asText(bp?.Code).trim();
        const name = asText(bp?.Name).trim();
        if (!code) return null;
        return { value: code, label: name ? `${code} - ${name}` : code };
      })
      .filter(Boolean);
  }, [bpOptions]);

  // Facturación desde el contrato (UI)
  const [facturas, setFacturas] = useState(() => ({}));
  const [facturarLoading, setFacturarLoading] = useState(false);
  const [facturarMsg, setFacturarMsg] = useState("");
  const [facturarError, setFacturarError] = useState("");

  // Cobranza en línea (opcional): registrar el cobro en el mismo paso que la factura.
  const [cobrarAhora, setCobrarAhora] = useState(false);
  const [cobroMoneda, setCobroMoneda] = useState("");
  const [cobroRate, setCobroRate] = useState("");
  const [cobroMedios, setCobroMedios] = useState([]);
  const [cuentasCobro, setCuentasCobro] = useState([]);
  // Monedas de SAP (OCRN). El código se elige de acá y no se tipea: el Service
  // Layer rechaza con -4008 "Invalid currency" cualquier código que no exista.
  const [monedasCobro, setMonedasCobro] = useState([]);
  const [cobroComentario, setCobroComentario] = useState("");
  // Si el usuario toca el comentario, dejamos de auto-completarlo con la selección.
  const [cobroComentarioEditado, setCobroComentarioEditado] = useState(false);

  // Detalle de facturas asociadas por cuota (acorde al SPGETFACTURASASOC)
  const [facturasByCuota, setFacturasByCuota] = useState(() => ({}));
  const [expandedCuotas, setExpandedCuotas] = useState(() => ({}));
  const [facturasLoadingByCuota, setFacturasLoadingByCuota] = useState(() => ({}));
  const [facturasErrorByCuota, setFacturasErrorByCuota] = useState(() => ({}));

  // PATCH "Envio" por factura
  const [envioUpdating, setEnvioUpdating] = useState(() => ({}));
  const [envioError, setEnvioError] = useState(() => ({}));

  // Extracto de cuentas (reporte ejecutivo)
  const [showExtracto, setShowExtracto] = useState(false);  // =====================
  // Mora (intereses por pago tardío)
  // =====================
  const [moraConfig, setMoraConfig] = useState({ intAnual: 0, diasTol: 0 });
  const [moraConfigLoading, setMoraConfigLoading] = useState(false);
  // Por lineId: { checked: false, monto: "0" }
  const [moraByLine, setMoraByLine] = useState(() => ({}));

  /**
   * Calcula los días de mora de una cuota dado su vencimiento (string).
   * Retorna 0 si no está vencida o si los días vencidos <= tolerancia.
   */
  const calcDiasMora = (fechaVenc) => {
    if (!fechaVenc) return 0;
    const s = String(fechaVenc).trim();

    const toTs = (y, m, d) => {
      const ts = Date.UTC(Number(y), Number(m) - 1, Number(d));
      return Number.isNaN(ts) ? null : ts;
    };

    let dueTs = null;

    // DD/MM/YYYY
    const mDmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mDmy) dueTs = toTs(mDmy[3], mDmy[2], mDmy[1]);

    // YYYY-MM-DD
    if (dueTs === null) {
      const mIso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (mIso) dueTs = toTs(mIso[1], mIso[2], mIso[3]);
    }

    if (dueTs === null) return 0;

    const now = new Date();
    const todayTs = toTs(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
    if (todayTs === null) return 0;

    const diffDays = Math.floor((todayTs - dueTs) / (1000 * 60 * 60 * 24));
    if (diffDays <= moraConfig.diasTol) return 0;
    return diffDays;
  };

  /**
   * Calcula el monto de mora para una cuota.
   * Fórmula: montoCuota * (tasaAnual / 365) * díasDeMora
   * (Siempre se calcula sobre el monto original de la cuota, no sobre el saldo.)
   */
  const calcMontoMora = (montoCuota, diasMora) => {
    if (!diasMora || diasMora <= 0 || !moraConfig.intAnual) return 0;
    const tasaDiaria = moraConfig.intAnual / 100 / 365;
    return Math.round(montoCuota * tasaDiaria * diasMora * 100) / 100;
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError("");
      setFacturarMsg("");
      setFacturarError("");
      try {
        const res = await apiClient.get(`/inmobiliaria/contratos/${docEntry}`,
          companyDb ? { params: { CompanyDB: companyDb } } : undefined
        );
        if (!alive) return;
        setData(res.data);

        // Inicializa estado de facturación por línea (desmarcado)
        const lines = Array.isArray(res.data?.REM_CONT_DCollection) ? res.data.REM_CONT_DCollection : [];
        const initial = {};
        lines.forEach((l) => {
          const lineId = l?.LineId;
          if (lineId === null || lineId === undefined) return;
          initial[String(lineId)] = { checked: false, monto: asText(l?.U_MONTOCUO) };
        });
        setFacturas(initial);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || "Error cargando contrato");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [docEntry]);

  useEffect(() => {
    // Carga de opciones para selector de unidades (con búsqueda)
    let alive = true;
    const loadUnidades = async () => {
      if (!unidadEditing) return;
      if (!companyDb) return;
      setUnidadesLoading(true);
      setUnidadesError("");
      try {
  // Nuevo SP: SPGETUNIDADES (DocEntry + detalle de unidad)
  const res = await apiClient.get("/inmobiliaria/unidades", { params: { CompanyDB: companyDb } });
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setUnidadesOptions(list);
      } catch (e) {
        if (!alive) return;
        setUnidadesError(e?.response?.data?.error || e?.message || "Error cargando unidades");
        setUnidadesOptions([]);
      } finally {
        if (alive) setUnidadesLoading(false);
      }
    };

    loadUnidades();
    return () => {
      alive = false;
    };
  }, [unidadEditing, companyDb]);

  useEffect(() => {
    // Carga de opciones para selector de cuentas contables (con búsqueda)
    let alive = true;
    const loadCtas = async () => {
      if (!ctaEditing) return;
      if (!companyDb) return;
      setCtaLoading(true);
      setCtaError("");
      try {
        const res = await apiClient.get("/inmobiliaria/cuentas-contables", { params: { CompanyDB: companyDb } });
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCtaOptions(list);
      } catch (e) {
        if (!alive) return;
        setCtaError(e?.response?.data?.error || e?.message || "Error cargando cuentas contables");
        setCtaOptions([]);
      } finally {
        if (alive) setCtaLoading(false);
      }
    };

    loadCtas();
    return () => {
      alive = false;
    };
  }, [ctaEditing, companyDb]);

  const ctaSelectOptions = useMemo(() => {
    return (ctaOptions || [])
      .map((r) => {
        const code = asText(r?.Code ?? r?.AcctCode ?? r?.AccountCode ?? r?.codigo ?? r?.CODIGO).trim();
        const name = asText(r?.Name ?? r?.AcctName ?? r?.AccountName ?? r?.nombre ?? r?.NOMBRE).trim();
        if (!code) return null;
        return { value: code, label: name ? `${code} - ${name}` : code, raw: r };
      })
      .filter(Boolean);
  }, [ctaOptions]);

  const startEditCuenta = () => {
    setCtaMsg("");
    setCtaError("");
    const current = asText(data?.U_CTA_FACT || "").trim();
    setCtaSelected(current ? { value: current, label: current } : null);
    setCtaEditing(true);
  };

  const cancelEditCuenta = () => {
    setCtaEditing(false);
    setCtaSelected(null);
    setCtaMsg("");
    setCtaError("");
  };

  const saveCuenta = async () => {
    const accountCode = asText(ctaSelected?.value).trim();
    if (!accountCode) {
      setCtaError("Seleccioná una cuenta contable");
      return;
    }

    setCtaSaving(true);
    setCtaError("");
    setCtaMsg("");
    try {
      await apiClient.patch(
        `/inmobiliaria/contratos/${docEntry}`,
        { U_CTA_FACT: accountCode },
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );

      const refreshed = await apiClient.get(
        `/inmobiliaria/contratos/${docEntry}`,
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );
      setData(refreshed.data);
      setCtaMsg("Cuenta contable actualizada");
      setCtaEditing(false);
    } catch (e) {
      setCtaError(e?.response?.data?.error || e?.message || "Error actualizando cuenta contable");
    } finally {
      setCtaSaving(false);
    }
  };

  const unidadSelectOptions = useMemo(() => {
    // SPGETUNIDADES devuelve el DocEntry de REM_UNI y detalle/descripción.
    // Soportamos variaciones de nombres de campos por si el SP cambia.
    return (unidadesOptions || [])
      .map((u) => {
        const doc = u?.DocEntry ?? u?.DOCENTRY ?? u?.docEntry ?? u?.docentry;
        const docEntry = doc === null || doc === undefined || doc === "" ? "" : String(doc).trim();
        if (!docEntry) return null;

        const desc = asText(
          u?.Descripcion ?? u?.DESCRIPCION ?? u?.Detalle ?? u?.DETALLE ?? u?.Unidad ?? u?.UNIDAD
        ).trim();
        const label = desc ? `${docEntry} - ${desc}` : docEntry;
        return { value: docEntry, label, raw: u };
      })
      .filter(Boolean);
  }, [unidadesOptions]);

  const startEditUnidad = () => {
    setUnidadMsg("");
    setUnidadesError("");
    const current = asText(data?.U_CODLOTE || "").trim();
    const currentLabel = asText(data?.U_UNIDAD_DESC || current).trim();
    setUnidadSelected(current ? { value: current, label: currentLabel } : null);
    setUnidadEditing(true);
  };

  const cancelEditUnidad = () => {
    setUnidadEditing(false);
    setUnidadSelected(null);
    setUnidadMsg("");
    setUnidadesError("");
  };

  const saveUnidad = async () => {
    const unidadDocEntry = asText(unidadSelected?.value).trim();
    if (!unidadDocEntry) {
      setUnidadesError("Seleccioná una unidad");
      return;
    }

    setUnidadSaving(true);
    setUnidadesError("");
    setUnidadMsg("");
    try {
      // Patch dirigido a campo de unidad en contrato.
      // Negocio: en REM_CONT se guarda el DocEntry de REM_UNI en U_CODLOTE.
      await apiClient.patch(
        `/inmobiliaria/contratos/${docEntry}`,
        { U_CODLOTE: unidadDocEntry },
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );

      const refreshed = await apiClient.get(
        `/inmobiliaria/contratos/${docEntry}`,
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );
      setData(refreshed.data);
      setUnidadMsg("Unidad actualizada");
      setUnidadEditing(false);
    } catch (e) {
      setUnidadesError(e?.response?.data?.error || e?.message || "Error actualizando unidad");
    } finally {
      setUnidadSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const loadBps = async () => {
      if (!companyDb) return;
      setBpOptionsLoading(true);
      setBpOptionsError("");
      try {
        const res = await apiClient.get("/inmobiliaria/clientes", { params: { CompanyDB: companyDb } });
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setBpOptions(list);
      } catch (e) {
        if (!alive) return;
        setBpOptionsError(e?.response?.data?.error || e?.message || "Error cargando socios");
        setBpOptions([]);
      } finally {
        if (alive) setBpOptionsLoading(false);
      }
    };

    loadBps();
    return () => {
      alive = false;
    };
  }, [companyDb]);

  const normalizeDistribFromContrato = (contratoData) => {
    const list = Array.isArray(contratoData?.REM_CONT_DACollection) ? contratoData.REM_CONT_DACollection : [];
    if (list.length > 0) {
      return list.map((r) => ({
        LineId: r?.LineId,
        U_CARDCODE: asText(r?.U_CARDCODE).trim(),
        // SAP valida este campo como: 'S'/'SI' o 'N'/'NO'.
        // Soportamos datos históricos (Y/N) en lectura, pero normalizamos a S/N.
        U_FACTURAR: (() => {
          const v = String(r?.U_FACTURAR ?? "S").trim().toUpperCase();
          if (["N", "NO", "0", "FALSE"].includes(v)) return "N";
          if (["S", "SI", "Y", "YES", "1", "TRUE"].includes(v)) return "S";
          return "S";
        })(),
        U_PORCENTAJE: r?.U_PORCENTAJE === null || r?.U_PORCENTAJE === undefined || r?.U_PORCENTAJE === "" ? 0 : Number(r.U_PORCENTAJE),
      }));
    }

    const titular = asText(contratoData?.U_CODSR).trim();
    return [{ LineId: undefined, U_CARDCODE: titular, U_FACTURAR: "S", U_PORCENTAJE: 100 }].filter((x) => x.U_CARDCODE);
  };

  const openDistribModal = () => {
    setDistribError("");
    setDistribMsg("");
    setDistribRows(normalizeDistribFromContrato(data));
    setShowDistribModal(true);
  };

  const addDistribRow = () => {
    setDistribRows((prev) => [...(prev || []), { LineId: undefined, U_CARDCODE: "", U_FACTURAR: "S", U_PORCENTAJE: 0 }]);
  };

  const removeDistribRow = (idx) => {
    setDistribRows((prev) => (prev || []).filter((_, i) => i !== idx));
  };

  const updateDistribRow = (idx, patch) => {
    setDistribRows((prev) => {
      const next = [...(prev || [])];
      next[idx] = { ...(next[idx] || {}), ...patch };
      return next;
    });
  };

  const saveDistribucion = async () => {
    setDistribSaving(true);
    setDistribError("");
    setDistribMsg("");
    try {
      const socios = (distribRows || [])
        .map((r) => ({
          LineId: r?.LineId,
          U_CARDCODE: asText(r?.U_CARDCODE).trim(),
          // Enviar en formato válido para SAP ('S'/'N')
          U_FACTURAR: (() => {
            const v = String(r?.U_FACTURAR ?? "S").trim().toUpperCase();
            if (["N", "NO", "0", "FALSE"].includes(v)) return "N";
            if (["S", "SI", "Y", "YES", "1", "TRUE"].includes(v)) return "S";
            return "S";
          })(),
          U_PORCENTAJE: Number(r?.U_PORCENTAJE || 0),
        }))
        // permitimos porcentaje 0, pero no permitimos CardCode vacío
        .filter((r) => !!r.U_CARDCODE);

      const res = await apiClient.put(
        `/inmobiliaria/contratos/${docEntry}/distribucion`,
        { socios },
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );

      setDistribMsg(res?.data?.message || "Distribución guardada");

      // Refresca contrato en pantalla
      const refreshed = await apiClient.get(
        `/inmobiliaria/contratos/${docEntry}`,
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );
      setData(refreshed.data);
      setDistribRows(normalizeDistribFromContrato(refreshed.data));
      setShowDistribModal(false);
    } catch (e) {
      setDistribError(e?.response?.data?.error || e?.message || "Error guardando distribución");
    } finally {
      setDistribSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const loadCondPagos = async () => {
      if (!companyDb) return;
      setCondPagosLoading(true);
      setCondPagosError("");
      try {
        const res = await apiClient.get(
          "/inmobiliaria/condiciones-pago",
          { params: { CompanyDB: companyDb } }
        );
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCondPagos(list);

        // Si no existe -1 en el SP, igual mantenemos el default -1.
        // Si el SP devuelve -1, lo dejamos seleccionado.
        const hasMinus1 = list.some((x) => Number(x?.GroupNum) === -1);
        if (!hasMinus1) {
          setPaymentGroupCode(-1);
        }
      } catch (e) {
        if (!alive) return;
        setCondPagosError(e?.response?.data?.error || e?.message || "Error cargando condiciones de pago");
        setCondPagos([]);
      } finally {
        if (alive) setCondPagosLoading(false);
      }
    };

    loadCondPagos();
    return () => {
      alive = false;
    };
  }, [companyDb]);

  // Carga configuración de mora (REM_PAR): U_INT_ANUAL y U_DIAS_TOL
  useEffect(() => {
    let alive = true;
    const loadMoraConfig = async () => {
      if (!companyDb) return;
      setMoraConfigLoading(true);
      try {
        const res = await apiClient.get("/inmobiliaria/configuracion", {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        const cfg = res?.data || {};
        setMoraConfig({
          intAnual: Number(cfg.U_INT_ANUAL) || 0,
          diasTol: Number(cfg.U_DIAS_TOL) || 0,
        });
      } catch {
        // Si falla, dejamos default 0 (sin mora)
      } finally {
        if (alive) setMoraConfigLoading(false);
      }
    };
    loadMoraConfig();
    return () => { alive = false; };
  }, [companyDb]);

  const moneda = data?.U_MONEDA;

  const lines = useMemo(() => {
    const list = data?.REM_CONT_DCollection;
    return Array.isArray(list) ? list : [];
  }, [data]);

  const billedByCuota = useMemo(() => {
    const db = data?.REM_CONT_DBCollection;
    const list = Array.isArray(db) ? db : [];
    const acc = {};
    list.forEach((r) => {
      const nro = Number(r?.U_NROCUOTA);
      if (Number.isNaN(nro)) return;
      const monto = Number(r?.U_MONTO || 0);
      acc[nro] = (acc[nro] || 0) + (Number.isNaN(monto) ? 0 : monto);
    });
    return acc;
  }, [data]);

  const isPastDue = (iso) => {
    if (!iso) return false;
    const s = String(iso).trim();

    const toMidnightTs = (yyyy, mm, dd) => {
      // Usamos timestamp UTC a medianoche para evitar desfases por timezone.
      const ts = Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd));
      return Number.isNaN(ts) ? null : ts;
    };

    // Formato esperado (SP): DD/MM/YYYY
    const mDmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mDmy) {
      const dd = mDmy[1];
      const mm = mDmy[2];
      const yyyy = mDmy[3];
      const dueTs = toMidnightTs(yyyy, mm, dd);
      if (dueTs === null) return false;
      const now = new Date();
      const todayTs = toMidnightTs(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
      return dueTs < todayTs;
    }

    // ISO: YYYY-MM-DD...
    const mIso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (mIso) {
      const yyyy = mIso[1];
      const mm = mIso[2];
      const dd = mIso[3];
      const dueTs = toMidnightTs(yyyy, mm, dd);
      if (dueTs === null) return false;
      const now = new Date();
      const todayTs = toMidnightTs(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
      return dueTs < todayTs;
    }

    // Desconocido: no marcamos como vencido.
    return false;
  };

  const getEstadoInfo = (line) => {
    const estado = String(line?.U_ESTADO || "").trim().toUpperCase();
    const nro = Number(line?.U_NROCUO);
    const total = Number(line?.U_MONTOCUO);
    const billed = billedByCuota?.[nro] || 0;
    const saldo = !Number.isNaN(total) ? Math.max(total - billed, 0) : null;

    if (estado === "Y") {
      // Estado final: si el SP marca 'Y', lo mostramos como facturado.
      // Puede ocurrir que no haya detalle asociado (REM_CONT_DBCollection) en algunos ambientes,
      // pero igual debe decir Facturado.
      return { label: "Facturado", className: "text-success fw-semibold", saldo: 0 };
    }

    if (estado === "P") return { label: "Parcialmente", className: "text-warning fw-semibold", saldo };

    // N: pendiente o vencido
    if (estado === "N") {
      if (isPastDue(line?.U_FECHAV)) return { label: "Vencido", className: "text-danger fw-semibold", saldo };
      return { label: "Pendiente", className: "text-muted fw-semibold", saldo };
    }

    return { label: asText(line?.U_ESTADO), className: "", saldo };
  };

  const onToggleCuota = (line) => {
    const lineId = String(line?.LineId ?? "");
    if (!lineId) return;

    const estado = String(line?.U_ESTADO || "").trim().toUpperCase();
    if (estado === "Y") return; // no permitir re-facturar

    setFacturas((prev) => {
      const current = prev?.[lineId] || { checked: false, monto: "" };
      const nextChecked = !current.checked;

      // Cuando se marca, precargamos el saldo pendiente (total - ya facturado).
      // Si no podemos calcularlo, usamos el total de la cuota como fallback.
      const nro = Number(line?.U_NROCUO);
      const total = Number(line?.U_MONTOCUO);
      const billed = billedByCuota?.[nro] || 0;
      const saldo = !Number.isNaN(total) ? Math.max(total - billed, 0) : null;
      return {
        ...prev,
        [lineId]: {
          checked: nextChecked,
          // Al checkear, precargamos el SALDO, pero dejamos editable.
          monto: nextChecked ? asText(saldo ?? line?.U_MONTOCUO) : current.monto,
        },
      };
    });
  };

  const onMontoChange = (lineId, value) => {
    setFacturas((prev) => ({
      ...prev,
      [lineId]: { ...(prev?.[lineId] || { checked: false, monto: "" }), monto: value },
    }));
  };

  /** Toggle checkbox de mora para una cuota. Al activar, precarga el monto calculado. */
  const onToggleMora = (line) => {
    const lineId = String(line?.LineId ?? "");
    if (!lineId) return;

    setMoraByLine((prev) => {
      const current = prev?.[lineId] || { checked: false, monto: "0" };
      const nextChecked = !current.checked;

      let monto = current.monto;
      if (nextChecked) {
        // Pre-calcular mora sobre el monto original de la cuota
        const total = Number(line?.U_MONTOCUO);
        const montoCuota = !Number.isNaN(total) ? total : 0;
        const diasMora = calcDiasMora(line?.U_FECHAV);
        monto = String(calcMontoMora(montoCuota, diasMora));
      }
      return { ...prev, [lineId]: { checked: nextChecked, monto } };
    });
  };

  const onMoraMontoChange = (lineId, value) => {
    setMoraByLine((prev) => ({
      ...prev,
      [lineId]: { ...(prev?.[lineId] || { checked: false, monto: "0" }), monto: value },
    }));
  };

  const selectedToBill = useMemo(() => {
    return lines
      .map((l) => {
        const id = String(l?.LineId ?? "");
        const st = facturas?.[id];
        if (!id || !st?.checked) return null;
        return {
          LineId: l.LineId,
          U_NROCUO: l?.U_NROCUO,
          U_FECHAV: l?.U_FECHAV,
          MontoAFacturar: st.monto,
        };
      })
      .filter(Boolean);
  }, [lines, facturas]);

  const totalFacturar = useMemo(() => {
    const sumBase = (selectedToBill || []).reduce((acc, x) => acc + Number(x?.MontoAFacturar || 0), 0);
    // Sumar montos de mora de las cuotas seleccionadas que tengan mora activada
    const sumMora = (selectedToBill || []).reduce((acc, x) => {
      const lineId = String(x?.LineId ?? "");
      const m = moraByLine?.[lineId];
      if (m?.checked) return acc + Number(m.monto || 0);
      return acc;
    }, 0);
    const total = sumBase + sumMora;
    return Number.isNaN(total) ? 0 : total;
  }, [selectedToBill, moraByLine]);

  // Saldo del contrato calculado solo con cuotas pendientes/vencidas (estado N)
  // y cuotas parciales (estado P).
  // Para cada cuota N/P: saldo = max(U_MONTOCUO - yaFacturado, 0)
  const saldoPendienteContrato = useMemo(() => {
    const arr = Array.isArray(lines) ? lines : [];
    const sum = arr.reduce((acc, l) => {
      const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
      if (estado !== "N" && estado !== "P") return acc;

      const nro = Number(l?.U_NROCUO);
      const total = Number(l?.U_MONTOCUO);
      const billed = billedByCuota?.[nro] || 0;
      const saldo = !Number.isNaN(total) ? Math.max(total - billed, 0) : 0;
      return acc + (Number.isNaN(saldo) ? 0 : saldo);
    }, 0);
    return Number.isNaN(sum) ? 0 : sum;
  }, [lines, billedByCuota]);

  // Cantidad total de cuotas del contrato (para el "N/X" del comentario del cobro).
  const totalCuotas = useMemo(() => {
    const n = Number(data?.U_CANTCUO);
    if (!Number.isNaN(n) && n > 0) return n;
    return Array.isArray(lines) ? lines.length : 0;
  }, [data, lines]);

  // Etiqueta corta de la unidad para el comentario del cobro: "DPTO 319".
  // El comentario va al JrnlMemo (máx. 50), por eso usamos solo el número.
  const dptoLabel = useMemo(() => {
    const desc = asText(data?.U_UNIDAD_DESC || "").trim();
    if (!desc) return "";
    // La descripción puede traer varios ítems separados por coma; usamos el primero.
    const primero = desc.split(",")[0].trim();
    const nro = extraerNroUnidad(primero);
    return (nro ? `DPTO ${nro}` : primero).toUpperCase();
  }, [data]);

  /**
   * Comentario automático del cobro, según las cuotas seleccionadas y la unidad.
   * Va todo en mayúsculas, con las cuotas primero y el departamento al final.
   *
   *   "CUOTA 2/18 PARCIAL CUOTA 3/18 DPTO 319"
   *   "CANC CUOTA 3/18 CUOTA 4/18 DPTO 319"
   *
   * Clasificación de cada cuota marcada:
   *  - ya tenía parcialidad facturada (billed > 0 o estado 'P'):
   *      cubre el saldo -> "CANC CUOTA"; si no -> "PARCIAL CUOTA"
   *  - sin facturación previa: cubre el saldo -> "CUOTA"; si no -> "PARCIAL CUOTA"
   */
  const cobroComentarioAuto = useMemo(() => {
    const cancelaciones = [];
    const completas = [];
    const parciales = [];

    (selectedToBill || []).forEach((x) => {
      const nro = Number(x?.U_NROCUO);
      if (Number.isNaN(nro)) return;

      const linea = (lines || []).find((l) => String(l?.LineId ?? "") === String(x?.LineId ?? ""));
      const estado = String(linea?.U_ESTADO || "").trim().toUpperCase();
      const total = Number(linea?.U_MONTOCUO);
      const billed = billedByCuota?.[nro] || 0;
      const saldo = Number.isNaN(total) ? 0 : Math.max(total - billed, 0);
      const monto = Number(x?.MontoAFacturar || 0);
      // Tolerancia de 1 centavo para no marcar parcial por redondeos.
      const cubreSaldo = saldo > 0 ? monto >= saldo - 0.01 : true;
      const etiqueta = `${nro}/${totalCuotas || "?"}`;

      if (billed > 0 || estado === "P") {
        (cubreSaldo ? cancelaciones : parciales).push(etiqueta);
      } else if (cubreSaldo) {
        completas.push(etiqueta);
      } else {
        parciales.push(etiqueta);
      }
    });

    // Dentro de cada bloque las cuotas se separan con " Y ", no con coma.
    const partes = [];
    if (cancelaciones.length) partes.push(`CANC CUOTA ${cancelaciones.join(" Y ")}`);
    if (completas.length) partes.push(`CUOTA ${completas.join(" Y ")}`);
    if (parciales.length) partes.push(`PARCIAL CUOTA ${parciales.join(" Y ")}`);

    // Cuotas primero, departamento al final. Los bloques van separados por espacio.
    return [partes.join(" "), dptoLabel].filter(Boolean).join(" ");
  }, [selectedToBill, lines, billedByCuota, totalCuotas, dptoLabel]);

  // --- Cobranza en línea (opcional) ---
  const esMonedaLocal = (m) => ["GS", "PYG", "G"].includes(String(m || "").trim().toUpperCase());
  const cobroFacturaExtranjera = !esMonedaLocal(moneda);
  const cobroExtranjera = !esMonedaLocal(cobroMoneda);
  const cobroMultimoneda = cobrarAhora && cobroMoneda && cobroMoneda.toUpperCase() !== String(moneda).toUpperCase();
  const cobroTotalMedios = useMemo(
    () => (cobroMedios || []).reduce((acc, m) => acc + (parseFloat(m.importe) || 0), 0),
    [cobroMedios]
  );
  const normCurCobro = (c) => {
    const u = String(c || "").trim().toUpperCase();
    return ["G", "GS", "PYG", "GUARANI", "GUARANIES"].includes(u) ? "GS" : u;
  };
  // Cuentas del medio de pago filtradas por la moneda del cobro (+ multimoneda).
  const cuentaCobroOpts = useMemo(() => {
    const target = normCurCobro(cobroMoneda);
    return (cuentasCobro || [])
      .filter((o) => {
        const cc = String(o.Currency || "").trim();
        if (!cc) return true;
        if (cc === "##") return true;
        return !target || normCurCobro(cc) === target;
      })
      .map((o) => ({
        value: o.Code,
        label: `${o.Code} - ${o.Name}${o.Currency && o.Currency !== "##" ? ` [${o.Currency}]` : o.Currency === "##" ? " [multi]" : ""}`,
      }));
  }, [cuentasCobro, cobroMoneda]);
  // Opciones del select de moneda del cobro. Siempre se incluye la moneda de la
  // factura, aunque OCRN no haya cargado, para que el valor por defecto sea válido.
  const monedaCobroOpts = useMemo(() => {
    const opts = (monedasCobro || [])
      .map((m) => ({ value: asText(m?.Code).trim(), label: asText(m?.Name).trim() }))
      .filter((o) => o.value);
    const invCur = asText(moneda).trim();
    if (invCur && !opts.some((o) => o.value.toUpperCase() === invCur.toUpperCase())) {
      opts.unshift({ value: invCur, label: "" });
    }
    return opts;
  }, [monedasCobro, moneda]);

  // Total esperado de los medios, en la moneda del cobro (saldo convertido vía TC).
  const cobroObjetivo = useMemo(() => {
    const tot = Number(totalFacturar || 0);
    const r = parseFloat(cobroRate) || 0;
    const invLocal = cobroFacturaExtranjera ? tot * r : tot;
    const obj = cobroExtranjera ? (r ? invLocal / r : 0) : invLocal;
    return Math.round((obj + Number.EPSILON) * 100) / 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFacturar, cobroRate, cobroFacturaExtranjera, cobroExtranjera]);
  const cobroCuadra = !cobrarAhora || (cobroObjetivo > 0 && Math.abs(cobroTotalMedios - cobroObjetivo) < 0.01);

  // Mantiene sincronizado el importe del único medio con el objetivo.
  useEffect(() => {
    if (!cobrarAhora) return;
    setCobroMedios((prev) => (prev.length === 1 ? [{ ...prev[0], importe: cobroObjetivo || "" }] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobroObjetivo, cobrarAhora]);

  const habilitarCobro = async (checked) => {
    setCobrarAhora(checked);
    if (!checked) return;
    setCobroMoneda(moneda);
    // Prellenar el comentario con la unidad + cuotas seleccionadas (editable antes de enviar).
    setCobroComentarioEditado(false);
    setCobroComentario(cobroComentarioAuto);
    // El importe lo completa el efecto según el objetivo (saldo convertido).
    setCobroMedios([{ tipo: "transferencia", cuenta: "", importe: "", referencia: "", numero: "", banco: "", vencimiento: "" }]);
    if (cuentasCobro.length === 0) {
      try {
        const res = await apiClient.get("/inmobiliaria/cuentas-contables", companyDb ? { params: { CompanyDB: companyDb } } : undefined);
        setCuentasCobro(Array.isArray(res?.data) ? res.data : []);
      } catch {
        /* si falla, los selects de cuenta quedan vacíos */
      }
    }
    if (monedasCobro.length === 0) {
      try {
        const res = await apiClient.get("/inmobiliaria/monedas", companyDb ? { params: { CompanyDB: companyDb } } : undefined);
        setMonedasCobro(Array.isArray(res?.data) ? res.data : []);
      } catch {
        /* si falla, el select queda solo con la moneda de la factura */
      }
    }
    // Prefill del TC con la cotización del día si la factura es en moneda extranjera.
    if (!esMonedaLocal(moneda)) {
      try {
        const res = await apiClient.get("/tipo-cambio", { params: { fecha: invoiceDate, moneda } });
        setCobroRate(res?.data?.encontrado ? String(res.data.tasa) : "");
      } catch {
        setCobroRate("");
      }
    } else {
      setCobroRate("");
    }
  };
  // Mantiene el comentario alineado con la selección de cuotas mientras el
  // usuario no lo haya editado a mano.
  useEffect(() => {
    if (!cobrarAhora || cobroComentarioEditado) return;
    setCobroComentario(cobroComentarioAuto);
  }, [cobrarAhora, cobroComentarioEditado, cobroComentarioAuto]);

  // Si cambia la fecha de facturación/cobro, re-traer la cotización de esa fecha.
  useEffect(() => {
    if (!cobrarAhora || esMonedaLocal(moneda)) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get("/tipo-cambio", { params: { fecha: invoiceDate, moneda } });
        if (alive) setCobroRate(res?.data?.encontrado ? String(res.data.tasa) : "");
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate]);

  const setCobroMedio = (i, campo, valor) =>
    setCobroMedios((prev) => prev.map((m, idx) => (idx === i ? { ...m, [campo]: valor } : m)));
  const addCobroMedio = () =>
    setCobroMedios((prev) => [...prev, { tipo: "efectivo", cuenta: "", importe: "", referencia: "", numero: "", banco: "", vencimiento: "" }]);
  const removeCobroMedio = (i) => setCobroMedios((prev) => prev.filter((_, idx) => idx !== i));

  const onFacturar = async () => {
    setFacturarLoading(true);
    setFacturarMsg("");
    setFacturarError("");
    try {
      if (selectedToBill.length === 0) {
        setFacturarError("Seleccioná al menos una cuota a facturar");
        return;
      }
      if (cobrarAhora && !cobroCuadra) {
        setFacturarError("La suma de los medios de pago debe igualar el total a facturar.");
        return;
      }

      const payload = {
        invoiceDate,
        paymentGroupCode,
        lineas: selectedToBill.map((x) => {
          const lineId = String(x.LineId ?? "");
          const mora = moraByLine?.[lineId];
          return {
            LineId: x.LineId,
            MontoAFacturar: x.MontoAFacturar,
            ...(mora?.checked ? { MontoMora: Number(mora.monto || 0) } : {}),
          };
        }),
        ...(cobrarAhora
          ? {
              cobro: {
                cobroCurrency: cobroMoneda,
                rate: cobroFacturaExtranjera ? cobroRate : 1,
                docDate: invoiceDate,
                comentario: cobroComentario,
                means: (cobroMedios || [])
                  .filter((m) => parseFloat(m.importe) > 0)
                  .map((m) => ({
                    tipo: m.tipo,
                    cuenta: m.cuenta,
                    importe: parseFloat(m.importe),
                    referencia: m.referencia,
                    numero: m.numero,
                    banco: m.banco,
                    vencimiento: m.vencimiento,
                  })),
              },
            }
          : {}),
      };

      const res = await apiClient.post(
        `/inmobiliaria/contratos/${docEntry}/facturar`,
        payload,
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );
      setFacturarMsg(res?.data?.message || "Solicitud de facturación enviada");

      // Resultado de la cobranza en línea (si se pidió).
      const cob = res?.data?.Cobranza;
      if (cob) {
        if (cob.ok) {
          const docs = (cob.creados || []).map((c) => c.DocEntry).join(", ");
          setFacturarMsg((prev) => `${prev} | Cobranza registrada (docs: ${docs || "—"}).`);
        } else if (cob.parcial) {
          const docs = (cob.creados || []).map((c) => c.DocEntry).join(", ");
          setFacturarError(
            `La factura se creó y la cobranza quedó PARCIAL: ${cob.error}. ` +
              `Documentos de pago ya creados: ${docs}. Revisá/anulá esos pagos en SAP antes de reintentar.`
          );
        } else {
          setFacturarError(`La factura se creó, pero la cobranza falló: ${cob.error}. Registrala desde 'Registrar cobro'.`);
        }
      }
      setCobrarAhora(false);
      setCobroMedios([]);
      setCobroComentario("");
      setCobroComentarioEditado(false);

      // Refrescamos el contrato para traer estados actualizados (Y/P) y DBCollection.
      const refreshed = await apiClient.get(`/inmobiliaria/contratos/${docEntry}`,
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );
      setData(refreshed.data);

      // Re-inicializa el formulario de facturación con el nuevo saldo pendiente por cuota.
      const refreshedLines = Array.isArray(refreshed.data?.REM_CONT_DCollection)
        ? refreshed.data.REM_CONT_DCollection
        : [];
      const refreshedDb = Array.isArray(refreshed.data?.REM_CONT_DBCollection)
        ? refreshed.data.REM_CONT_DBCollection
        : [];

      const billedAcc = {};
      refreshedDb.forEach((r) => {
        const nro = Number(r?.U_NROCUOTA);
        if (Number.isNaN(nro)) return;
        const monto = Number(r?.U_MONTO || 0);
        billedAcc[nro] = (billedAcc[nro] || 0) + (Number.isNaN(monto) ? 0 : monto);
      });

      const nextFacturas = {};
      refreshedLines.forEach((l) => {
        const lineId = l?.LineId;
        if (lineId === null || lineId === undefined) return;

        const nro = Number(l?.U_NROCUO);
        const total = Number(l?.U_MONTOCUO);
        const saldo = !Number.isNaN(total) ? Math.max(total - (billedAcc[nro] || 0), 0) : l?.U_MONTOCUO;

        nextFacturas[String(lineId)] = { checked: false, monto: asText(saldo) };
      });
      setFacturas(nextFacturas);
    } catch (e) {
      setFacturarError(e?.response?.data?.error || e?.message || "Error facturando");
    } finally {
      setFacturarLoading(false);
    }
  };

  const toggleCuotaFacturas = async (line) => {
    const nro = Number(line?.U_NROCUO);
    if (Number.isNaN(nro)) return;

    const nextExpanded = !expandedCuotas?.[nro];
    setExpandedCuotas((prev) => ({ ...prev, [nro]: nextExpanded }));
    if (!nextExpanded) return; // Solo cargar al expandir

  // Si ya está cargado, no volver a pedir.
  if (Array.isArray(facturasByCuota?.[nro])) return;

    try {
      setFacturasErrorByCuota((prev) => ({ ...prev, [nro]: "" }));
      setFacturasLoadingByCuota((prev) => ({ ...prev, [nro]: true }));

      // El SP necesita DocEntry de la factura + nro de cuota.
      // Como una cuota puede tener múltiples facturas (parcialidades), tomamos todas las
      // facturas registradas en REM_CONT_DBCollection para esa cuota y pedimos el detalle
      // por cada DocEntry.
      const dbList = Array.isArray(data?.REM_CONT_DBCollection) ? data.REM_CONT_DBCollection : [];
      const invoiceDocEntries = dbList
        .filter((r) => Number(r?.U_NROCUOTA) === nro)
        .map((r) => Number(r?.U_NRODOCU))
        .filter((x) => !Number.isNaN(x) && x > 0);

      // Si no hay facturas registradas para esa cuota, devolvemos vacío (sin error).
      if (invoiceDocEntries.length === 0) {
        setFacturasByCuota((prev) => ({ ...prev, [nro]: [] }));
        return;
      }

      const uniqueDocEntries = Array.from(new Set(invoiceDocEntries));
      const results = await Promise.all(
        uniqueDocEntries.map(async (docEntryFactura) => {
          const res = await apiClient.get(
            `/inmobiliaria/facturas/${docEntryFactura}/cuotas/${nro}/detalle`,
            companyDb ? { params: { CompanyDB: companyDb } } : undefined
          );
          return Array.isArray(res.data) ? res.data : [];
        })
      );

      const merged = results.flat();
      setFacturasByCuota((prev) => ({ ...prev, [nro]: merged }));
    } catch (e) {
      setFacturasErrorByCuota((prev) => ({
        ...prev,
        [nro]: e?.response?.data?.error || e?.message || "Error obteniendo facturas asociadas",
      }));
      setFacturasByCuota((prev) => ({ ...prev, [nro]: [] }));
    } finally {
      setFacturasLoadingByCuota((prev) => ({ ...prev, [nro]: false }));
    }
  };

  const formatEnvio = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s) return "";
    if (["S", "SI", "TRUE", "1"].includes(s.toUpperCase())) return "SI";
    if (["N", "NO", "FALSE", "0"].includes(s.toUpperCase())) return "NO";
    return s;
  };

  const isEnvioSi = (v) => formatEnvio(v) === "SI";
  const isEnvioNo = (v) => formatEnvio(v) === "NO";

  const patchEnvioFactura = async ({ cuotaNro, rowIndex, row, nextValue }) => {
    const docEntryFactura = Number(row?.DocEntry);
    if (Number.isNaN(docEntryFactura) || docEntryFactura <= 0) {
      setEnvioError((prev) => ({
        ...prev,
        [`${cuotaNro}:${rowIndex}`]: "DocEntry inválido para esta factura (ver SPGETFACTURASASOC)",
      }));
      return;
    }

    const key = `${cuotaNro}:${rowIndex}`;
    setEnvioUpdating((prev) => ({ ...prev, [key]: true }));
    setEnvioError((prev) => ({ ...prev, [key]: "" }));
    try {
      await apiClient.patch(
        `/inmobiliaria/facturas/${docEntryFactura}/envio`,
        { envio: nextValue },
        companyDb ? { params: { CompanyDB: companyDb } } : undefined
      );

      // Actualizamos el estado local para que el switch quede en SI y deshabilitado.
      setFacturasByCuota((prev) => {
        const list = Array.isArray(prev?.[cuotaNro]) ? [...prev[cuotaNro]] : [];
        if (!list[rowIndex]) return prev;
        list[rowIndex] = { ...list[rowIndex], Envio: "S" };
        return { ...prev, [cuotaNro]: list };
      });
    } catch (e) {
      setEnvioError((prev) => ({
        ...prev,
        [key]: e?.response?.data?.error || e?.message || "Error actualizando envío",
      }));
    } finally {
      setEnvioUpdating((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-0">Contrato Nro {docEntry}</h2>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setShowExtracto(true)}
            disabled={loading || !data}
            title="Generar Extracto de Cuentas"
          >
            Extracto de Cuentas
          </button>
          <Link className="btn btn-outline-secondary" to="/inmobiliaria/contratos">
            Volver
          </Link>
        </div>
      </div>

      {loading && <div className="alert alert-info">Cargando...</div>}
      {!loading && error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              {distribMsg ? <div className="alert alert-success py-2">{distribMsg}</div> : null}

              <div className="row g-3 align-items-end">
                <div className="col-12 col-md">
                  <label className="form-label">Estado</label>
                  <input className="form-control" value={asText(data?.U_ESTADO)} readOnly />
                </div>
                <div className="col-12 col-md">
                  <label className="form-label">Moneda</label>
                  <input className="form-control" value={asText(data?.U_MONEDA)} readOnly />
                </div>
                <div className="col-12 col-md">
                  <label className="form-label">Fecha Contrato</label>
                  <input className="form-control" value={formatDate(data?.U_FECHAC)} readOnly />
                </div>
                <div className="col-12 col-md">
                  <label className="form-label">Vencimiento</label>
                  <input className="form-control" value={formatDate(data?.U_FECHAV)} readOnly />
                </div>
              </div>

              <div className="row g-3 align-items-end mt-2">
                <div className="col-12 col-md-4">
                  <label className="form-label">Cliente</label>
                  <input
                    className="form-control"
                    value={[asText(data?.U_CODSR), asText(data?.U_CODSR_NAME)].filter(Boolean).join(" - ")}
                    readOnly
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Unidad</label>

                  {!unidadEditing ? (
                    <div className="input-group">
                      <input className="form-control" value={asText(data?.U_UNIDAD_DESC || data?.U_CODLOTE)} readOnly />
                      <button type="button" className="btn btn-outline-secondary" onClick={startEditUnidad}>
                        Editar
                      </button>
                    </div>
                  ) : (
                    <>
                      <Select
                        isClearable
                        isSearchable
                        isLoading={unidadesLoading}
                        options={unidadSelectOptions}
                        value={unidadSelected}
                        onChange={(opt) => setUnidadSelected(opt)}
                        placeholder={unidadesLoading ? "Cargando unidades..." : "Buscar unidad..."}
                      />
                      {unidadesError ? <div className="form-text text-danger">{unidadesError}</div> : null}
                      {unidadMsg ? <div className="form-text text-success">{unidadMsg}</div> : null}
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={unidadSaving || unidadesLoading}
                          onClick={saveUnidad}
                        >
                          {unidadSaving ? "Guardando..." : "Guardar"}
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={cancelEditUnidad} disabled={unidadSaving}>
                          Cancelar
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="col-12 col-md-2 d-flex align-items-end">
                  <button type="button" className="btn btn-outline-primary w-100" onClick={openDistribModal}>
                    Distribución de facturas
                  </button>
                </div>
              </div>

              <div className="row g-3 align-items-end mt-2">
                <div className="col-12 col-md-3">
                  <label className="form-label">Entrega Inicial</label>
                  <input className="form-control" value={formatMoney(data?.U_ENTINICIAL, moneda)} readOnly />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Monto Cuota</label>
                  <input className="form-control" value={formatMoney(data?.U_MONTCUO, moneda)} readOnly />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Cantidad Cuotas</label>
                  <input className="form-control" value={asText(data?.U_CANTCUO)} readOnly />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Cuenta contable</label>

                  {!ctaEditing ? (
                    <div className="input-group">
                      <input className="form-control" value={asText(data?.U_CTA_FACT)} readOnly />
                      <button type="button" className="btn btn-outline-secondary" onClick={startEditCuenta}>
                        Editar
                      </button>
                    </div>
                  ) : (
                    <>
                      <Select
                        isClearable
                        isSearchable
                        isLoading={ctaLoading}
                        options={ctaSelectOptions}
                        value={ctaSelected}
                        onChange={(opt) => setCtaSelected(opt)}
                        placeholder={ctaLoading ? "Cargando cuentas..." : "Buscar cuenta..."}
                      />
                      {ctaError ? <div className="form-text text-danger">{ctaError}</div> : null}
                      {ctaMsg ? <div className="form-text text-success">{ctaMsg}</div> : null}
                      <div className="d-flex gap-2 mt-2">
                        <button type="button" className="btn btn-primary" disabled={ctaSaving || ctaLoading} onClick={saveCuenta}>
                          {ctaSaving ? "Guardando..." : "Guardar"}
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={cancelEditCuenta} disabled={ctaSaving}>
                          Cancelar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="row g-3 align-items-end mt-2">
                <div className="col-12 col-md-3">
                  <label className="form-label">Saldo</label>
                  <input className="form-control" value={formatMoney(saldoPendienteContrato, moneda)} readOnly />
                </div>
              </div>

              <div className="row g-3 align-items-end mt-2">
                <div className="col-12 col-md-4">
                  <label className="form-label">Fecha de factura</label>
                  <input
                    type="date"
                    className="form-control"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-5">
                  <label className="form-label">Condición de pago</label>
                  <select
                    className="form-select"
                    value={String(paymentGroupCode)}
                    onChange={(e) => setPaymentGroupCode(Number(e.target.value))}
                    disabled={condPagosLoading}
                  >
                    <option value="-1">Contado</option>
                    {condPagos
                      .filter((x) => Number(x?.GroupNum) !== -1)
                      .map((x) => (
                        <option key={String(x?.GroupNum)} value={String(x?.GroupNum)}>
                          {asText(x?.PymntGroup) || asText(x?.GroupNum)}
                        </option>
                      ))}
                  </select>
                  {condPagosError ? (
                    <div className="form-text text-danger">{condPagosError}</div>
                  ) : null}
                  {!condPagosLoading && companyDb && condPagos.length === 0 && !condPagosError ? (
                    <div className="form-text text-muted">No se encontraron condiciones de pago (SPGETCONDPAGOS).</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {showDistribModal && (
            <>
              <div className="modal fade show" style={{ display: "block" }} tabIndex={-1} role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Distribución de facturas</h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setShowDistribModal(false)}
                      />
                    </div>
                    <div className="modal-body">
                      {distribError ? <div className="alert alert-danger">{distribError}</div> : null}
                      {bpOptionsError ? <div className="alert alert-warning">{bpOptionsError}</div> : null}

                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th style={{ width: "55%" }}>Socio de negocio</th>
                              <th style={{ width: "20%" }}>Facturar</th>
                              <th style={{ width: "15%" }}>%</th>
                              <th style={{ width: "10%" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {(distribRows || []).map((r, idx) => {
                              const facturar = (() => {
                                const v = String(r?.U_FACTURAR ?? "S").trim().toUpperCase();
                                return !["N", "NO", "0", "FALSE"].includes(v);
                              })();
                              return (
                                <tr key={idx}>
                                  <td>
                                    <Select
                                      classNamePrefix="react-select"
                                      isClearable
                                      isDisabled={bpOptionsLoading}
                                      isLoading={bpOptionsLoading}
                                      placeholder="Buscar socio..."
                                      options={bpSelectOptions}
                                      value={
                                        bpSelectOptions.find((o) => o.value === asText(r?.U_CARDCODE).trim()) ||
                                        (asText(r?.U_CARDCODE).trim()
                                          ? { value: asText(r?.U_CARDCODE).trim(), label: asText(r?.U_CARDCODE).trim() }
                                          : null)
                                      }
                                      onChange={(opt) => updateDistribRow(idx, { U_CARDCODE: opt?.value || "" })}
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={facturar}
                                        onChange={(e) =>
                                          updateDistribRow(idx, { U_FACTURAR: e.target.checked ? "S" : "N" })
                                        }
                                      />
                                      <label className="form-check-label">{facturar ? "SI" : "NO"}</label>
                                    </div>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={Number.isFinite(Number(r?.U_PORCENTAJE)) ? String(r?.U_PORCENTAJE) : "0"}
                                      min={0}
                                      step={0.01}
                                      onChange={(e) => updateDistribRow(idx, { U_PORCENTAJE: e.target.value })}
                                    />
                                  </td>
                                  <td className="text-end">
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => removeDistribRow(idx)}
                                      title="Quitar"
                                    >
                                      X
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-between">
                        <button type="button" className="btn btn-outline-secondary" onClick={addDistribRow}>
                          Agregar línea
                        </button>
                        <div className="text-muted small">
                          Filas con % 0 se guardan pero no se usarán para facturar.
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowDistribModal(false)}
                        disabled={distribSaving}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveDistribucion}
                        disabled={distribSaving}
                      >
                        {distribSaving ? "Guardando..." : "Guardar"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show" />
            </>
          )}

          <div className="card shadow-sm">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <strong>Cuotas</strong>
              <div className="text-muted small">
                Seleccionadas: {selectedToBill.length} | Total a facturar (con mora): {formatMoney(totalFacturar, moneda)}
              </div>
            </div>
            <div className="card-body">
              {facturarError && <div className="alert alert-danger">{facturarError}</div>}
              {facturarMsg && <div className="alert alert-success">{facturarMsg}</div>}

              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead className="table-primary">
                    <tr>
                      <th style={{ width: 60 }} />
                      <th style={{ width: 110 }}>Nro</th>
                      <th style={{ width: 160 }}>Vencimiento</th>
                      <th className="text-end" style={{ width: 180 }}>
                        Monto
                      </th>
                      <th className="text-end" style={{ width: 180 }}>
                        Saldo
                      </th>
                      <th className="text-end" style={{ width: 220 }}>
                        Monto a Facturar
                      </th>
                      <th style={{ width: 80 }} className="text-center">
                        Mora
                      </th>
                      <th className="text-end" style={{ width: 180 }}>
                        Monto Mora
                      </th>
                      <th style={{ width: 100 }}>Días Mora</th>
                      <th style={{ width: 120 }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          Sin detalle de cuotas
                        </td>
                      </tr>
                    )}

                    {lines.map((l) => {
                      const lineId = String(l?.LineId ?? "");
                      const st = facturas?.[lineId] || { checked: false, monto: "" };
                      const estadoInfo = getEstadoInfo(l);
                      const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
                      const disabledBilling = estado === "Y";
                      const nroCuota = Number(l?.U_NROCUO);
                      const isExpanded = !Number.isNaN(nroCuota) ? !!expandedCuotas?.[nroCuota] : false;
                      const detLoading = !Number.isNaN(nroCuota) ? !!facturasLoadingByCuota?.[nroCuota] : false;
                      const detError = !Number.isNaN(nroCuota) ? (facturasErrorByCuota?.[nroCuota] || "") : "";
                      const detList = !Number.isNaN(nroCuota) ? (facturasByCuota?.[nroCuota] || null) : null;
                      return (
                        <React.Fragment key={lineId || Math.random()}>
                          <tr
                            onClick={() => toggleCuotaFacturas(l)}
                            style={{ cursor: "pointer" }}
                            title="Click para ver/ocultar facturas asociadas"
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!st.checked}
                                onChange={() => onToggleCuota(l)}
                                disabled={disabledBilling}
                              />
                            </td>
                            <td>
                              <span className="me-2 text-muted">{isExpanded ? "▾" : "▸"}</span>
                              {asText(l?.U_NROCUO)}
                            </td>
                            <td>{formatDate(l?.U_FECHAV)}</td>
                            <td className="text-end">{formatMoney(l?.U_MONTOCUO, moneda)}</td>
                            <td className="text-end">{formatMoney(estadoInfo.saldo, moneda)}</td>
                            <td className="text-end" onClick={(e) => e.stopPropagation()}>
                              <input
                                className="form-control form-control-sm text-end"
                                value={asText(st.monto)}
                                onChange={(e) => onMontoChange(lineId, e.target.value)}
                                disabled={!st.checked || disabledBilling}
                                placeholder={asText(estadoInfo.saldo ?? l?.U_MONTOCUO)}
                              />
                            </td>
                            {/* Mora: checkbox + monto editable + días */}
                            {(() => {
                              const diasMora = disabledBilling ? 0 : calcDiasMora(l?.U_FECHAV);
                              const moraState = moraByLine?.[lineId] || { checked: false, monto: "0" };
                              const showMora = diasMora > 0;
                              return (
                                <>
                                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                    {showMora ? (
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={!!moraState.checked}
                                        onChange={() => onToggleMora(l)}
                                      />
                                    ) : null}
                                  </td>
                                  <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                    {showMora ? (
                                      <input
                                        className="form-control form-control-sm text-end"
                                        value={moraState.checked ? asText(moraState.monto) : ""}
                                        onChange={(e) => onMoraMontoChange(lineId, e.target.value)}
                                        disabled={!moraState.checked}
                                        placeholder="0"
                                      />
                                    ) : null}
                                  </td>
                                  <td className={diasMora > 0 ? "text-danger fw-semibold" : "text-muted"}>
                                    {diasMora > 0 ? diasMora : "—"}
                                  </td>
                                </>
                              );
                            })()}
                            <td className={estadoInfo.className}>{estadoInfo.label}</td>
                          </tr>

                          {isExpanded && (
                            <tr>
                              <td colSpan={10} className="bg-light">
                                {detLoading && (
                                  <div className="d-flex align-items-center gap-2 py-2">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                    <span className="text-muted">Cargando facturas asociadas...</span>
                                  </div>
                                )}

                                {!detLoading && detError && (
                                  <div className="alert alert-danger mb-2">{detError}</div>
                                )}

                                {!detLoading && !detError && Array.isArray(detList) && detList.length === 0 && (
                                  <div className="text-muted py-2">Sin facturas asociadas</div>
                                )}

                                {!detLoading && !detError && Array.isArray(detList) && detList.length > 0 && (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-2">
                                      <thead className="table-secondary">
                                        <tr>
                                          <th style={{ width: 140 }}>Fecha</th>
                                          <th style={{ width: 140 }}>NroDocumento</th>
                                          <th style={{ width: 160 }}>NroFactura</th>
                                          <th className="text-end" style={{ width: 140 }}>
                                            Monto
                                          </th>
                                          <th style={{ width: 120 }}>Envio</th>
                                          <th style={{ minWidth: 220 }}>CDC</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detList.map((r, idx) => (
                                          <tr key={idx}>
                                            <td>{formatDate(r?.Fecha)}</td>
                                            <td title={r?.DocEntry ? `DocEntry: ${asText(r?.DocEntry)}` : undefined}>{asText(r?.NroDocumento)}</td>
                                            <td>{asText(r?.NroFactura)}</td>
                                            <td className="text-end">{formatMoney(r?.Monto, moneda)}</td>
                                            <td>
                                              {(() => {
                                                const envioSi = isEnvioSi(r?.Envio);
                                                const envioNo = isEnvioNo(r?.Envio);
                                                const key = `${nroCuota}:${idx}`;
                                                const updating = !!envioUpdating?.[key];
                                                const errMsg = envioError?.[key] || "";

                                                // Caso raro (valor desconocido): mostramos el valor tal cual.
                                                if (!envioSi && !envioNo) return formatEnvio(r?.Envio);

                                                // Si ya está en SI, el switch queda tildado e inactivo:
                                                // las facturas ya se crean con U_ENVIAR='S', no hay que
                                                // volver a marcarlo. Si está en NO, permite pasarlo a SI.
                                                return (
                                                  <div>
                                                    <div className="form-check form-switch m-0">
                                                      <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={envioSi}
                                                        disabled={envioSi || updating}
                                                        onChange={() =>
                                                          patchEnvioFactura({
                                                            cuotaNro: nroCuota,
                                                            rowIndex: idx,
                                                            row: r,
                                                            nextValue: "S",
                                                          })
                                                        }
                                                      />
                                                      <label className="form-check-label">
                                                        {envioSi ? "SI" : "NO"}
                                                      </label>
                                                    </div>
                                                    {updating && (
                                                      <div className="small text-muted">Actualizando...</div>
                                                    )}
                                                    {!updating && errMsg && (
                                                      <div className="small text-danger">{errMsg}</div>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            <td>{asText(r?.CDC)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Cobranza en línea (opcional) */}
              <div className="border rounded p-2 mb-3 mt-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="chk-cobrar-ahora"
                    checked={cobrarAhora}
                    onChange={(e) => habilitarCobro(e.target.checked)}
                    disabled={selectedToBill.length === 0}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="chk-cobrar-ahora">
                    Registrar cobranza al facturar
                  </label>
                </div>

                {cobrarAhora && (
                  <div className="mt-2">
                    <div className="row g-2 align-items-end">
                      <div className="col-md-3">
                        <label className="form-label small mb-0">Moneda del cobro</label>
                        <select
                          className="form-select form-select-sm"
                          value={cobroMoneda}
                          onChange={(e) => setCobroMoneda(e.target.value)}
                        >
                          {monedaCobroOpts.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label ? `${o.value} - ${o.label}` : o.value}
                            </option>
                          ))}
                        </select>
                      </div>
                      {cobroFacturaExtranjera && (
                        <div className="col-md-3">
                          <label className="form-label small mb-0">Tipo de cambio {cobroMultimoneda ? "(ajustable)" : ""}</label>
                          <input
                            type="number"
                            step="0.000001"
                            className="form-control form-control-sm"
                            value={cobroRate}
                            onChange={(e) => setCobroRate(e.target.value)}
                            placeholder="Ej: 7500"
                          />
                        </div>
                      )}
                      <div className="col-md-5">
                        <div className={`alert py-1 mb-0 small ${cobroCuadra ? "alert-success" : "alert-warning"}`}>
                          Total a facturar: <strong>{formatMoney(totalFacturar, moneda)}</strong>
                          {cobroMultimoneda && (
                            <> → A cobrar: <strong>{formatMoney(cobroObjetivo, cobroMoneda)}</strong></>
                          )}
                          {" "}— Medios ({cobroMoneda || moneda}):{" "}
                          <strong>{formatMoney(cobroTotalMedios, cobroMoneda || moneda)}</strong>
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label small mb-0 d-flex justify-content-between">
                          <span>Comentario (unidad y cuotas — editable)</span>
                          {cobroComentarioEditado && cobroComentarioAuto ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 small"
                              onClick={() => {
                                setCobroComentarioEditado(false);
                                setCobroComentario(cobroComentarioAuto);
                              }}
                            >
                              Restaurar automático
                            </button>
                          ) : null}
                        </label>
                        <input
                          className="form-control form-control-sm"
                          value={cobroComentario}
                          onChange={(e) => {
                            setCobroComentarioEditado(true);
                            setCobroComentario(e.target.value);
                          }}
                          placeholder="Va al comentario del pago y al memo del asiento (JrnlMemo, máx. 50)"
                        />
                        {cobroComentario.length > 50 ? (
                          <div className="form-text text-warning small mb-0">
                            {cobroComentario.length} caracteres: el memo del asiento (JrnlMemo) se trunca a 50.
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {(cobroMedios || []).map((m, i) => (
                      <div className="row g-2 align-items-end mt-1" key={i}>
                        <div className="col-md-2">
                          <select className="form-select form-select-sm" value={m.tipo} onChange={(e) => setCobroMedio(i, "tipo", e.target.value)}>
                            <option value="efectivo">Efectivo</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>
                        {m.tipo !== "cheque" ? (
                          <div className="col-md-4">
                            <SearchSelect
                              options={cuentaCobroOpts}
                              value={m.cuenta}
                              onChange={(v) => setCobroMedio(i, "cuenta", v)}
                              placeholder="Cuenta donde entró..."
                            />
                          </div>
                        ) : (
                          <>
                            <div className="col-md-2">
                              <input className="form-control form-control-sm" placeholder="N° cheque" value={m.numero} onChange={(e) => setCobroMedio(i, "numero", e.target.value)} />
                            </div>
                            <div className="col-md-2">
                              <input className="form-control form-control-sm" placeholder="Banco" value={m.banco} onChange={(e) => setCobroMedio(i, "banco", e.target.value)} />
                            </div>
                          </>
                        )}
                        <div className="col-md-2">
                          <input type="number" step="0.01" className="form-control form-control-sm" placeholder="Importe" value={m.importe} onChange={(e) => setCobroMedio(i, "importe", e.target.value)} />
                        </div>
                        <div className="col-md-1">
                          <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeCobroMedio(i)} disabled={cobroMedios.length <= 1}>×</button>
                        </div>
                      </div>
                    ))}
                    <button className="btn btn-outline-secondary btn-sm mt-2" type="button" onClick={addCobroMedio}>+ Agregar medio</button>
                    {cobroMultimoneda && (
                      <div className="text-muted small mt-1">Cobro en otra moneda: se generan 2 documentos (a cuenta + transitoria).</div>
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={onFacturar}
                  disabled={facturarLoading || (cobrarAhora && !cobroCuadra)}
                >
                  {facturarLoading ? "Facturando..." : cobrarAhora ? "Facturar y cobrar" : "Facturar seleccionadas"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de Extracto de Cuentas (reporte ejecutivo).
          Reutiliza data/lines/billedByCuota/bpOptions/facturasByCuota ya cargados
          para evitar llamadas redundantes al backend. */}
      <ExtractoCuentasContratoModal
        show={showExtracto && !!data}
        onClose={() => setShowExtracto(false)}
        data={data}
        lines={lines}
        billedByCuota={billedByCuota}
        moneda={moneda}
        docEntry={docEntry}
        companyDb={companyDb}
        bpOptions={bpOptions}
        facturasByCuota={facturasByCuota}
        setFacturasByCuota={setFacturasByCuota}
        formatDate={formatDate}
        formatMoney={formatMoney}
        calcDiasMora={calcDiasMora}
      />
    </div>
  );
};

export default GestionInmobiliariaContratoDetalle;
